import { Button, Chip, Grid, TextField, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BlockIcon from '@material-ui/icons/Block';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LinkIcon from '@material-ui/icons/Link';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SendIcon from '@material-ui/icons/Send';
import SmsOutlinedIcon from '@material-ui/icons/SmsOutlined';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FilterPanel, hasActiveFilters } from 'components/reusable/filter-panel';
import { List } from 'components/reusable/list';
import { ReusableSelect as Select } from 'components/reusable/select';
import { Tick } from 'components/reusable/tick';
import { posthog } from 'posthog';
import { isAuthorized, SUPPORT_ADMIN_PERMISSIONS } from 'security/authorization';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { copyInvite, loadInvites, postInvite, resendInvite } from 'store/sagas/sagas';
import { capitalize } from 'utils/format';
import { useDebounce } from 'utils/hooks';
import { humanDate, humanPhone } from 'utils/ui.utils';
import { extractUrl, isQueriesSame, pushFiltersSearchPath, rawParseQuery, transformQueryTypes } from 'utils/url';
import { useIsMobile } from 'utils/window';
import './candidates.css';
const shouldAllowCandidateCreation = (newCandidate, candidatesFeature) => {
    const hasBasicInfo = newCandidate.firstName && newCandidate.lastName;
    if (candidatesFeature) {
        return !(hasBasicInfo && newCandidate.email && newCandidate.phone);
    }
    const canSend = !newCandidate.send ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        (newCandidate.contact === 'email' && newCandidate.email) ||
        (newCandidate.contact === 'phone' && newCandidate.phone);
    return !(hasBasicInfo && canSend);
};
const initialData = {
    pageSize: 50,
    page: 1,
    id: null,
    lastName: null,
    email: null,
    phone: null,
    accountGroupSid: null
};
const mapData = {
    pageSize: 'number',
    page: 'number'
};
// The verification-link channel chips, in display order. SMS maps to the
// `phone` contact; Email maps to the `email` contact.
const CHANNEL_CHIPS = [
    { key: 'dont-send', label: "Don't send", Icon: BlockIcon },
    { key: 'sms', label: 'SMS', Icon: SmsOutlinedIcon },
    { key: 'email', label: 'Email', Icon: MailOutlineIcon }
];
const getActiveChip = (send, contact) => {
    if (!send)
        return 'dont-send';
    return contact === 'email' ? 'email' : 'sms';
};
const useStyles = makeStyles(() => ({
    main_div: ({ isMobile }) => ({
        width: isMobile ? '100%' : 'auto',
        display: isMobile ? 'flex' : 'inherit',
        flexDirection: isMobile ? 'column' : 'inherit'
    })
}));
export const Candidates = () => {
    const { state, asyncDispatch, dispatch } = useGlobalState();
    const navigate = useNavigate();
    const { search } = useLocation();
    // Vouched internal staff only: isAdmin (System Admin) or isSupport
    // (Support Admin). isUserAnyAdmin below is the customer's account admin.
    const isSupportAdmin = state.user
        ? isAuthorized(state.user, SUPPORT_ADMIN_PERMISSIONS)
        : false;
    const [loading, setLoading] = useState(false);
    const [isUserAnyAdmin, setIsUserAnyAdmin] = useState(false);
    const [groupDropdownItems, setGroupDropdownItems] = useState([]);
    const [newCandidate, setNewCandidate] = useState({
        phone: null,
        email: null,
        send: true,
        contact: 'phone',
        firstName: '',
        lastName: '',
        internalId: null,
        accountGroupSid: null,
        callbackURL: null,
        dob: null,
        ial2CrossCheckId: null
    });
    useEffect(() => {
        if (!state.user) {
            return;
        }
        const isUsrAnyAdmn = state.user.isAccountAdmin;
        const userGroupId = state.user.groupId;
        const userGroup = userGroupId
            ? ((state.accountGroups ?? []).find((g) => `${g.id}` === userGroupId) ??
                null)
            : null;
        setIsUserAnyAdmin(isUsrAnyAdmn);
        let dropdownItems = [];
        if (isUsrAnyAdmn) {
            dropdownItems = state.accountGroups ?? [];
        }
        else if (userGroup) {
            dropdownItems = [userGroup];
        }
        setGroupDropdownItems(dropdownItems);
        setNewCandidate((currentValue) => {
            return {
                ...currentValue,
                accountGroupSid: isUsrAnyAdmn ? null : (userGroup?.sid ?? null)
            };
        });
    }, [state.user, state.accountGroups]);
    const toAccountGroupName = (sid) => {
        if (!state.accountGroups || !sid) {
            return null;
        }
        const found = state.accountGroups.find((a) => a.sid === sid);
        return found ? found.name : null;
    };
    const qsData = transformQueryTypes(mapData, rawParseQuery());
    const [filter, setFilter] = useState({
        ...initialData,
        ...qsData
    });
    const debouncedFilter = useDebounce(filter, 250);
    const updateFilter = (newFilter) => {
        setFilter({ ...initialData, ...newFilter });
    };
    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const canClearFilters = hasActiveFilters([
        filter.id,
        filter.lastName,
        filter.phone,
        filter.email,
        filter.accountGroupSid
    ]);
    const [invites, setInvites] = useState({
        items: [],
        total: 0,
        totalPages: 0
    });
    const resetFields = () => {
        setNewCandidate((currentValue) => {
            return {
                phone: null,
                email: null,
                contact: 'phone',
                send: currentValue.send,
                firstName: '',
                lastName: '',
                internalId: null,
                accountGroupSid: currentValue.accountGroupSid,
                dob: null,
                ial2CrossCheckId: null
            };
        });
    };
    useEffect(() => {
        const newQsData = transformQueryTypes(mapData, rawParseQuery(search.replace(/\?/, '')));
        if (!isQueriesSame(newQsData, filter)) {
            updateFilter(newQsData);
        }
    }, [search]);
    useEffect(() => {
        const { isInitial } = pushFiltersSearchPath({
            filter: debouncedFilter,
            initialData: initialData,
            mapData,
            path: '/account/candidates'
        });
        if (!isOpenFilters && !isInitial) {
            setIsOpenFilters(true);
        }
        setLoading(true);
        asyncDispatch(loadInvites(debouncedFilter))
            .then(setInvites, console.error)
            .finally(() => {
            setLoading(false);
        });
    }, [debouncedFilter]);
    const handleGoToCandidateJob = useCallback((id) => {
        navigate(`/account/jobs?ids=${id}`);
    }, [navigate]);
    const handleChipSelect = useCallback((chip) => {
        setNewCandidate((cand) => ({
            ...cand,
            send: chip !== 'dont-send',
            ...(chip !== 'dont-send' && {
                contact: chip === 'email' ? 'email' : 'phone'
            })
        }));
    }, []);
    const handleChange = useCallback((e) => {
        setNewCandidate((cand) => ({
            ...cand,
            [e.target.name]: e.target.value
        }));
    }, []);
    const fixCandidate = (candidate) => {
        return { ...candidate };
    };
    const handlePageChange = (_event, page) => {
        updateFilter({
            ...filter,
            page
        });
    };
    const handleChangeFilters = useCallback((e) => {
        updateFilter({
            ...filter,
            [e.target.name]: e.target.value
        });
    }, [filter]);
    const isMobile = useIsMobile();
    const isDisabled = shouldAllowCandidateCreation(newCandidate, !!state.account?.candidateContactInfoRequired);
    const classes = useStyles({ isMobile });
    const activeChip = getActiveChip(newCandidate.send ?? true, newCandidate.contact ?? null);
    return (React.createElement("div", { className: classes.main_div },
        React.createElement("h1", { className: "g-page-name" }, "Candidates"),
        React.createElement("div", { className: "filters-container-container" },
            React.createElement("div", { className: "filters-container" },
                React.createElement("div", { className: "u-margin-top-medium" },
                    React.createElement("div", { className: "candidates__form-section" },
                        React.createElement("div", { className: "candidates__section-label" }, "Applicant"),
                        React.createElement(Grid, { container: true, spacing: 3 },
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 },
                                React.createElement(TextField, { className: "g-full-width", label: "First Name", name: "firstName", onChange: handleChange, value: newCandidate.firstName })),
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 },
                                React.createElement(TextField, { className: "g-full-width", label: "Last Name", name: "lastName", onChange: handleChange, value: newCandidate.lastName })),
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 },
                                React.createElement(TextField, { className: "g-full-width", label: "Date of Birth (Optional)", name: "dob", onChange: handleChange, placeholder: "MM/DD/YYYY", value: newCandidate.dob ?? '' })))),
                    React.createElement("div", { className: "candidates__form-section" },
                        React.createElement("div", { className: "candidates__section-label" }, "Verification link"),
                        React.createElement("div", { className: "candidates__chip-row" }, CHANNEL_CHIPS.map(({ key, label, Icon }) => (React.createElement(Chip, { "aria-pressed": activeChip === key, className: `candidates__chip${activeChip === key ? ' candidates__chip--selected' : ''}`, icon: React.createElement(Icon, { fontSize: "small" }), key: key, label: label, onClick: () => {
                                handleChipSelect(key);
                            } })))),
                        React.createElement(Grid, { container: true, spacing: 3 },
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 },
                                React.createElement(TextField, { className: "g-full-width", label: activeChip === 'sms' ? 'Phone · link sent here' : 'Phone', name: "phone", onChange: handleChange, value: newCandidate.phone ?? '' })),
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 },
                                React.createElement(TextField, { className: "g-full-width", label: activeChip === 'email'
                                        ? 'Email · link sent here'
                                        : 'Email', name: "email", onChange: handleChange, value: newCandidate.email ?? '' }))),
                        activeChip === 'dont-send' && (React.createElement("div", { className: "candidates__note" },
                            React.createElement(InfoOutlinedIcon, { fontSize: "small" }),
                            React.createElement("span", null, "No link will be sent. You can copy the invitation link from the candidates list and share it manually. Contact details are saved to the record.")))),
                    React.createElement("div", { className: "candidates__form-section" },
                        React.createElement("div", { className: "candidates__section-label" }, "Advanced options"),
                        React.createElement(Grid, { container: true, spacing: 3 },
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 },
                                React.createElement(TextField, { className: "g-full-width", label: "Callback URL (Optional)", name: "callbackURL", onChange: handleChange, value: newCandidate.callbackURL ?? '' })),
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 },
                                React.createElement(TextField, { className: "g-full-width", label: "Internal Id (Optional)", name: "internalId", onChange: handleChange, value: newCandidate.internalId ?? '' })),
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 },
                                React.createElement(TextField, { className: "g-full-width", label: "IAL2 Cross Check Id (Optional)", name: "ial2CrossCheckId", onChange: handleChange, value: newCandidate.ial2CrossCheckId ?? '' })),
                            React.createElement(Grid, { item: true, md: 3, sm: 6, xs: 12 }, isUserAnyAdmin ? (React.createElement(Select, { className: "g-full-width", isClearable: true, items: groupDropdownItems.map((a) => ({
                                    id: a.sid,
                                    name: a.name
                                })), label: "Account Group", name: "accountGroupSid", onChange: handleChange, value: newCandidate.accountGroupSid ?? '' })) : (React.createElement(TextField, { InputProps: {
                                    readOnly: true
                                }, className: "g-full-width", label: "Account Group", value: groupDropdownItems[0]?.name ?? 'No group' }))))),
                    React.createElement(Button, { className: "u-margin-top-large u-margin-bottom-large", color: "primary", disabled: isDisabled, onClick: () => {
                            asyncDispatch(postInvite(fixCandidate(newCandidate)))
                                .then(() => {
                                posthog.capture('candidate_created', {
                                    delivery_channel: activeChip,
                                    has_account_group: Boolean(newCandidate.accountGroupSid)
                                });
                                dispatch(setMessages({
                                    value: 'Candidate created!',
                                    severity: 'success'
                                }));
                                resetFields();
                                asyncDispatch(loadInvites({}))
                                    .then((res) => {
                                    setInvites(res);
                                })
                                    .catch(console.error);
                            })
                                .catch(() => {
                                resetFields();
                            });
                        }, variant: "contained" }, "Create")))),
        React.createElement(FilterPanel, { canClear: canClearFilters, onClear: () => {
                updateFilter({});
            }, onOpenChange: setIsOpenFilters, open: isOpenFilters },
            React.createElement("div", { className: "filters-row" },
                React.createElement(TextField, { label: "Id", name: "id", onChange: handleChangeFilters, value: filter.id ?? '' }),
                React.createElement(TextField, { label: "Last Name", name: "lastName", onChange: handleChangeFilters, value: filter.lastName ?? '' }),
                React.createElement(Select, { isClearable: true, items: groupDropdownItems.map((a) => ({ id: a.sid, name: a.name })), label: "Account Group", name: "accountGroupSid", onChange: handleChangeFilters, value: filter.accountGroupSid ?? '' })),
            React.createElement("div", { className: "filters-row" },
                React.createElement(TextField, { label: "Phone", name: "phone", onChange: handleChangeFilters, value: filter.phone ?? '' }),
                React.createElement(TextField, { label: "Email", name: "email", onChange: handleChangeFilters, value: filter.email ?? '' }),
                React.createElement(Select, { isClearable: false, items: [
                        { id: 10, name: '10' },
                        { id: 25, name: '25' },
                        { id: 50, name: '50' }
                    ], label: "Size", name: "pageSize", onChange: handleChangeFilters, value: filter.pageSize }))),
        React.createElement(List, { columns: [
                {
                    name: 'Status',
                    xs: 2,
                    customRenderValue: ({ status }) => {
                        let displayState = 'active';
                        status = capitalize(status ?? '');
                        // let color = '#f4d348';
                        const FAILED = ['failed', 'undelivered'];
                        const COMPLETED = ['completed'];
                        if (FAILED.includes(status.toLowerCase())) {
                            displayState = 'rejected';
                        }
                        else if (COMPLETED.includes(status.toLowerCase())) {
                            displayState = 'approved';
                        }
                        const dot = (React.createElement(Tick, { state: displayState, style: { marginRight: 8, marginTop: 6 } }));
                        return (React.createElement("div", { className: "list_span_dot" },
                            dot,
                            React.createElement("div", { className: "text-ellipsis" }, status)));
                    }
                },
                {
                    name: 'Id',
                    xs: 2,
                    customRenderValue: ({ id, accountGroupSid, internalId }) => {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: "text-ellipsis" }, id),
                            React.createElement("div", { className: "text-ellipsis" }, toAccountGroupName(accountGroupSid)),
                            React.createElement("div", { className: "text-ellipsis", "data-ph-mask": true }, internalId)));
                    }
                },
                {
                    name: 'Name',
                    replayMasked: true,
                    xs: 2,
                    customRenderValue: ({ firstName, lastName }) => {
                        return (React.createElement("div", { style: { width: '100%' } },
                            React.createElement("div", { className: "text-ellipsis" }, firstName),
                            React.createElement("div", { className: "text-ellipsis" }, lastName)));
                    }
                },
                {
                    name: 'Contact',
                    replayMasked: true,
                    xs: 2,
                    customRenderValue: ({ contact, phone, email }) => {
                        if (!contact)
                            return null;
                        const first = contact === 'phone' ? humanPhone(phone) : email;
                        const second = contact === 'phone' ? email : humanPhone(phone);
                        return (React.createElement("div", { style: { width: '100%' } },
                            React.createElement("div", { className: "text-ellipsis" },
                                "To: ",
                                first),
                            React.createElement("div", { className: "text-ellipsis" }, second ?? null)));
                    }
                },
                {
                    name: 'Updated',
                    xs: 2,
                    isClickable: false,
                    customRenderValue: ({ updatedAt }) => (React.createElement("span", null, humanDate(updatedAt)))
                },
                {
                    xs: 2,
                    name: '',
                    isClickable: false,
                    customRenderValue: ({ id, status, contact, phone, email }) => {
                        const isCompleted = status === 'completed';
                        const resendTip = contact === 'email'
                            ? `Resend email to ${email}`
                            : `Resend SMS to ${humanPhone(phone)}`;
                        return (!isCompleted && (React.createElement("div", { className: "candidates__row-actions" },
                            contact && (React.createElement(Tooltip, { placement: "top", title: resendTip },
                                React.createElement("button", { "aria-label": resendTip, className: "candidates__act-btn", onClick: () => {
                                        asyncDispatch(resendInvite(id))
                                            .then(() => {
                                            posthog.capture('candidate_invite_resent', {
                                                delivery_channel: contact
                                            });
                                            dispatch(setMessages({
                                                value: 'Sent!',
                                                severity: 'success'
                                            }));
                                        })
                                            .catch(console.error);
                                    }, type: "button" },
                                    React.createElement(SendIcon, { fontSize: "inherit" })))),
                            React.createElement(Tooltip, { placement: "top", title: "Copy verification link" },
                                React.createElement("button", { "aria-label": "Copy verification link", className: "candidates__act-btn", onClick: () => {
                                        asyncDispatch(copyInvite(id))
                                            .then((res) => {
                                            const url = res.message
                                                ? extractUrl(res.message)
                                                : null;
                                            if (!url) {
                                                dispatch(setMessages({
                                                    value: 'This invite has no verification link',
                                                    severity: 'error'
                                                }));
                                                return;
                                            }
                                            window.navigator.clipboard
                                                .writeText(url)
                                                .catch(console.error);
                                            posthog.capture('verification_link_copied');
                                            dispatch(setMessages({
                                                value: 'Copied!',
                                                severity: 'success'
                                            }));
                                        })
                                            .catch(console.error);
                                    }, type: "button" },
                                    React.createElement(LinkIcon, { fontSize: "inherit" }))),
                            isSupportAdmin && (React.createElement(Tooltip, { placement: "top", title: "Open verification link" },
                                React.createElement("button", { "aria-label": "Open verification link", className: "candidates__act-btn", onClick: () => {
                                        // Open the tab in the click handler itself;
                                        // deferring window.open until the request resolves
                                        // loses the user gesture and the browser blocks it
                                        // as a popup.
                                        const tab = window.open('', '_blank');
                                        if (!tab) {
                                            dispatch(setMessages({
                                                value: 'Allow pop-ups to open the verification link',
                                                severity: 'error'
                                            }));
                                            return;
                                        }
                                        tab.opener = null;
                                        asyncDispatch(copyInvite(id))
                                            .then((res) => {
                                            const url = res.message
                                                ? extractUrl(res.message)
                                                : null;
                                            if (!url) {
                                                tab.close();
                                                dispatch(setMessages({
                                                    value: 'This invite has no verification link',
                                                    severity: 'error'
                                                }));
                                                return;
                                            }
                                            tab.location.href = url;
                                        })
                                            .catch((error) => {
                                            tab.close();
                                            console.error(error);
                                            dispatch(setMessages({
                                                value: 'Could not load the verification link',
                                                severity: 'error'
                                            }));
                                        });
                                    }, type: "button" },
                                    React.createElement(OpenInNewIcon, { fontSize: "inherit" })))))));
                    }
                }
            ], handlePageChange: handlePageChange, isClickable: (item) => {
                const LINKABLE = ['completed', 'active', 'started'];
                return !!item.status && LINKABLE.includes(item.status.toLowerCase());
            }, items: invites.items, label: "candidates", listSource: "candidates", loading: loading, onClick: (item) => {
                handleGoToCandidateJob(item.id);
            }, page: filter.page, total: invites.total, totalPages: invites.totalPages })));
};
